import React, { useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import {
  useListConversations,
  ConversationListItem,
  usePinConversation,
} from '../../../../api/conversations';
import { useChatStore } from '../../store/chatStore';
import { LoadingDots } from '../LoadingDots';
import { useModal } from '../../../../contexts/ModalContext';
import { useConversationHover } from '../../../../hooks/useConversationHover';
import { Bell, Pin, MessageSquare } from 'lucide-react';
import { ConversationMenu } from './ConversationMenu';
import { conversationSourceLabels } from '../../constants';
import './Sidebar.css';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

// Source configuration (All first, then shared labels)
const sourceOptions = (t: TFunction<'pleri/chat'>) =>
  [
    { value: undefined, label: t('allConversations') },
    ...Object.entries(conversationSourceLabels(t)).map(([value, label]) => ({
      value,
      label,
    })),
  ] as const;

// Source display configuration
const sourceDisplay = (t: TFunction<'pleri/chat'>) => ({
  'plerion-platform': {
    badge: t('platform'),
    color: 'bg-orange-100 dark:bg-gray-700 text-orange-700',
  },
  email: {
    badge: t('common:email'),
    color: 'bg-blue-100 dark:bg-blue-900 text-blue-700',
  },
  task: {
    badge: t('common:task'),
    color: 'bg-green-100 dark:bg-green-900 text-green-700',
  },
  slack: {
    badge: t('common:slack'),
    color: 'bg-purple-100 dark:bg-purple-900 text-purple-700',
  },
  mcp: {
    badge: 'MCP',
    color: 'bg-teal-100 dark:bg-teal-900 text-teal-700',
  },
  spotlight: {
    badge: t('common:spotlight'),
    color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700',
  },
});

type ConversationSourceKey = keyof ReturnType<typeof sourceDisplay>;

// Conversation group order for chronological display
const CONVERSATION_GROUP_ORDER = [
  'Pinned',
  'Needs attention',
  'Today',
  'Yesterday',
  'This week',
  'This month',
  'Older',
] as const;

// Group names double as keys, so they stay English and translate at render.
const CONVERSATION_GROUP_LABEL_KEYS = {
  Pinned: 'pinned',
  'Needs attention': 'needsAttention',
  Today: 'today',
  Yesterday: 'yesterday',
  'This week': 'thisWeek',
  'This month': 'thisMonth',
  Older: 'older',
} as const;

// Pin limit constant
const PIN_LIMIT = 10;

interface RecentChatsListProps {
  /** Size variant - 'compact' for sidebar, 'full' for full page */
  size?: 'compact' | 'full';
  /** Show source filter dropdown */
  showSourceFilter?: boolean;
  /** Custom className for the container */
  className?: string;
}

export const RecentChatsList: React.FC<RecentChatsListProps> = ({
  size = 'compact',
  showSourceFilter = true,
  className = '',
}) => {
  const { t } = useTranslation('pleri/chat');
  const location = useLocation();
  const [selectedSource, setSelectedSource] = useState<string | undefined>(
    undefined,
  );

  const { openDeleteModal, openRenameModal } = useModal();
  const { mutate: pinConversation } = usePinConversation();

  // Conversation hover and tooltip logic
  const {
    showTooltip,
    handleConversationMouseEnter,
    handleConversationMouseLeave,
    clearHoverState,
    shouldShowTooltip,
    isHovered,
  } = useConversationHover();

  const { data, isLoading } = useListConversations({
    source: selectedSource,
    pinned: false,
  });
  const { data: pinnedData } = useListConversations({
    pinned: true,
  });
  const { data: needsAttentionData } = useListConversations({
    followUpNotify: true,
    source: selectedSource,
  });

  // Extract conversation ID from current path
  const getCurrentConversationId = () => {
    const pathSegments = location.pathname.split('/');
    const conversationIndex = pathSegments.findIndex(
      (segment) => segment === 'c',
    );
    return conversationIndex !== -1 &&
      conversationIndex + 1 < pathSegments.length
      ? pathSegments[conversationIndex + 1]
      : null;
  };

  const currentConversationId = getCurrentConversationId();

  const loadingConversationId = useChatStore(
    (state) => state.loadingConversationId,
  );

  const handleConversationHover = (conversationId: string) => {
    // Prefetch conversation data on hover if not already loaded
    const state = useChatStore.getState();
    if (!state.conversations[conversationId] && !state.loadingConversationId) {
      // Prefetch with a small delay to avoid excessive API calls
      setTimeout(() => {
        if (!useChatStore.getState().conversations[conversationId]) {
          useChatStore.getState().loadConversationFromDb(conversationId);
        }
      }, 300);
    }
  };

  const handleDeleteConversation = (conversationId: string) => {
    clearHoverState();
    openDeleteModal(conversationId);
  };

  const handlePinConversation = (conversationId: string, isPinned: boolean) => {
    pinConversation({ conversationId, pinned: !isPinned });
  };

  const handleRenameConversation = (conversationId: string, title: string) => {
    clearHoverState();
    openRenameModal(conversationId, title);
  };

  // Filter out conversations with externalId starting with 'question-chat-'
  const filteredConversations = (data?.conversations || []).filter(
    (conversation) =>
      !conversation.externalId ||
      !conversation.externalId.startsWith('question-chat-'),
  );

  // Filter pinned conversations
  const filteredPinnedConversations = (pinnedData?.conversations || []).filter(
    (conversation) =>
      !conversation.externalId ||
      !conversation.externalId.startsWith('question-chat-'),
  );

  // Check if there are multiple source types to determine if badges should be shown
  const allConversations = [
    ...filteredConversations,
    ...filteredPinnedConversations,
  ];
  const uniqueSources = new Set(
    allConversations.map((conversation) => conversation.source).filter(Boolean),
  );
  const shouldShowBadges = uniqueSources.size > 1;

  // Check if pin limit is reached
  const isPinLimitReached = filteredPinnedConversations.length >= PIN_LIMIT;

  // Helper function to render conversation items
  const renderConversationItem = (conversation: ConversationListItem) => {
    const isLoading = loadingConversationId === conversation.conversationId;
    const isActive = currentConversationId === conversation.conversationId;
    const title = conversation.title || t('newChatFallbackTitle');
    const shouldShowTooltipForTitle = shouldShowTooltip(title);
    const isConversationHovered = isHovered(conversation.conversationId);

    return (
      <div
        onMouseEnter={() => {
          handleConversationHover(conversation.conversationId);
          handleConversationMouseEnter(conversation.conversationId, title);
        }}
        onMouseLeave={handleConversationMouseLeave}
        className="relative"
      >
        <Link
          key={conversation.conversationId}
          to="/c/$conversationId"
          params={{
            conversationId: conversation.conversationId,
          }}
          className={`conversation-item group block w-full text-left text-sm transition-all duration-300 ${
            size === 'full'
              ? `rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${
                  isLoading
                    ? 'pointer-events-none border-orange-200 bg-orange-50 text-orange-900 opacity-75 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                    : isActive
                      ? 'border-orange-200 bg-orange-50 text-orange-900 shadow-md dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                      : 'text-gray-700 hover:border-gray-300 hover:shadow-md dark:text-gray-300 dark:hover:border-gray-600'
                }`
              : `rounded-[4.5px] px-2 py-1.5 ${
                  isLoading
                    ? 'pointer-events-none border border-orange-200 bg-orange-50 text-orange-900 opacity-75 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                    : isActive
                      ? 'border border-orange-200 bg-orange-50 text-orange-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }`
          }`}
        >
          <div className="relative flex h-[24px] min-w-0 items-center gap-2">
            <span className="block flex-1 truncate text-sm font-medium">
              {title}
            </span>
            {conversation.pinned && (
              <Pin className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            )}
            {shouldShowBadges &&
              conversation.source &&
              sourceDisplay(t)[
                conversation.source as ConversationSourceKey
              ] && (
                <span
                  className={`hidden flex-shrink-0 rounded px-1 py-0.5 text-xs font-medium group-hover:inline-block ${sourceDisplay(t)[conversation.source as ConversationSourceKey].color}`}
                >
                  {
                    sourceDisplay(t)[
                      conversation.source as ConversationSourceKey
                    ].badge
                  }
                </span>
              )}
            {conversation.followUpNotify && (
              <div className="relative hover:opacity-100">
                <Bell className="peer h-4 w-4 flex-shrink-0 text-orange-500" />
                <div className="pointer-events-none absolute top-1/2 right-full z-[9999] mr-2 -translate-y-1/2 transform opacity-0 transition-opacity peer-hover:opacity-100">
                  <div className="rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white shadow-lg">
                    {t('needsYourAttention')}
                    <div className="absolute top-1/2 left-full -translate-y-1/2 transform border-4 border-transparent border-l-gray-800"></div>
                  </div>
                </div>
              </div>
            )}
            {isLoading && (
              <div className="h-3 w-3 flex-shrink-0 animate-spin rounded-full border border-gray-300 border-t-[#FF732E] dark:border-gray-600"></div>
            )}
            {/* Hover actions - only show on hover */}
            <div
              className="hidden items-center group-hover:flex"
              onMouseEnter={(e) => {
                e.stopPropagation();
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
              }}
            >
              <ConversationMenu
                onDelete={() =>
                  handleDeleteConversation(conversation.conversationId)
                }
                onPin={() =>
                  handlePinConversation(
                    conversation.conversationId,
                    conversation.pinned || false,
                  )
                }
                onRename={() =>
                  handleRenameConversation(
                    conversation.conversationId,
                    conversation.title || '',
                  )
                }
                isPinned={conversation.pinned || false}
                isPinDisabled={!conversation.pinned && isPinLimitReached}
                pinLimitMessage={`Only ${PIN_LIMIT} chats can be pinned at a time`}
              />
            </div>
          </div>
        </Link>
        {/* Tooltip for long titles */}
        {shouldShowTooltipForTitle && isConversationHovered && showTooltip && (
          <div className="absolute top-full left-0 z-[9999] mt-1 w-full">
            <div className="max-w-xs rounded-md bg-gray-800 px-3 py-2 text-sm text-white shadow-lg">
              {title}
              <div className="absolute top-0 left-4 h-0 w-0 -translate-y-full transform border-4 border-transparent border-b-gray-800"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const groupConversationsByTime = (conversations: ConversationListItem[]) => {
    const groups: { [key: string]: ConversationListItem[] } = {};
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);

    // Add "Pinned" group if there are pinned conversations
    if (filteredPinnedConversations.length > 0) {
      groups['Pinned'] = filteredPinnedConversations;
    }

    // Add "Needs attention" group if there are conversations needing attention
    if (
      needsAttentionData?.conversations &&
      needsAttentionData.conversations.length > 0
    ) {
      groups['Needs attention'] = needsAttentionData.conversations;
    }

    // Group regular conversations by time (excluding those that need attention and pinned conversations)
    const needsAttentionIds = new Set(
      needsAttentionData?.conversations?.map((c) => c.conversationId) || [],
    );
    const pinnedIds = new Set(
      filteredPinnedConversations.map((c) => c.conversationId),
    );

    conversations.forEach((conversation) => {
      // Skip conversations that are already in the "Needs attention" section or pinned
      if (
        needsAttentionIds.has(conversation.conversationId) ||
        pinnedIds.has(conversation.conversationId)
      ) {
        return;
      }

      // Use statusUpdatedAt if available, fallback to updatedAt
      const dateString = conversation.statusUpdatedAt || conversation.updatedAt;
      const date = new Date(dateString);
      const conversationDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const diffInDays = Math.floor(
        (today.getTime() - conversationDay.getTime()) / (24 * 60 * 60 * 1000),
      );

      let groupKey;
      if (conversationDay.getTime() === today.getTime()) {
        groupKey = 'Today';
      } else if (conversationDay.getTime() === yesterday.getTime()) {
        groupKey = 'Yesterday';
      } else if (diffInDays < 7) {
        groupKey = 'This week';
      } else if (diffInDays < 30) {
        groupKey = 'This month';
      } else {
        groupKey = 'Older';
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(conversation);
    });

    return groups;
  };

  // Size-based styling
  const containerClasses =
    size === 'full'
      ? `flex-1 overflow-y-auto bg-white dark:bg-gray-900 ${className}`
      : `custom-scrollbar flex-1 overflow-y-auto border-t border-gray-200/80 dark:border-gray-700/80 ${className}`;
  const contentPadding = size === 'full' ? 'px-4 py-6' : 'px-2 py-2';
  const headerClasses =
    size === 'full'
      ? 'mb-6 flex items-center justify-between'
      : 'flex items-center justify-between gap-2';
  const titleClasses =
    size === 'full'
      ? 'text-base font-semibold text-gray-900 dark:text-gray-100'
      : 'whitespace-nowrap px-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400';
  const selectClasses =
    size === 'full'
      ? 'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2 pr-10 pl-4 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:border-gray-400 dark:hover:border-gray-500 focus:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200 transition-colors'
      : 'rounded border border-gray-200 dark:border-gray-700 bg-transparent py-0.5 pr-7 pl-1.5 text-xs text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-200';

  return (
    <div className={containerClasses}>
      <div className={contentPadding}>
        {showSourceFilter && (
          <div className={headerClasses}>
            <h3 className={titleClasses}>{t('recentChats')}</h3>
            <select
              value={selectedSource || ''}
              data-testid="recent-chats-source-select"
              onChange={(e) => setSelectedSource(e.target.value || undefined)}
              className={selectClasses}
            >
              {sourceOptions(t).map((option) => (
                <option key={option.value || 'all'} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-3">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <LoadingDots />
              <span>{t('common:loading')}</span>
            </div>
          </div>
        ) : filteredConversations.length === 0 &&
          filteredPinnedConversations.length === 0 &&
          (!needsAttentionData?.conversations ||
            needsAttentionData.conversations.length === 0) ? (
          <div className="py-3 text-center">
            <MessageSquare className="mx-auto mb-1 h-4 w-4 text-gray-300" />
            <p className="mb-0.5 text-xs text-gray-500 dark:text-gray-400">
              {t('noConversationsYet')}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('startAConversation')}
            </p>
          </div>
        ) : (
          <div className={size === 'full' ? 'space-y-6' : 'space-y-3'}>
            {(() => {
              const groups = groupConversationsByTime(filteredConversations);

              return CONVERSATION_GROUP_ORDER.filter(
                (groupName) =>
                  groups[groupName] && groups[groupName].length > 0,
              ).map((groupName, index) => (
                <div
                  key={groupName}
                  className={index === 0 && size === 'compact' ? 'mt-2' : ''}
                >
                  <h4
                    className={`mb-3 flex items-center gap-1.5 ${
                      size === 'full'
                        ? 'text-sm font-semibold text-gray-700 dark:text-gray-300'
                        : 'px-1 text-xs font-medium text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {groupName === 'Pinned' && (
                      <Pin
                        className={size === 'full' ? 'h-4 w-4' : 'h-3 w-3'}
                      />
                    )}
                    {t(CONVERSATION_GROUP_LABEL_KEYS[groupName])}
                  </h4>
                  <div
                    className={
                      size === 'full' ? 'space-y-2' : 'space-y-0.5 px-1'
                    }
                  >
                    {groups[groupName].map((conversation) =>
                      renderConversationItem(conversation),
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    </div>
  );
};
